import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import SiteChrome from './SiteChrome';

const inputClass = 'w-full rounded-[3px] border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 transition-all';
const labelClass = 'block text-xs font-medium text-gray-700 mb-1.5';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    remember: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationMessage, setVerificationMessage] = useState('');
  const [resendingEmail, setResendingEmail] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setVerificationMessage('');

    try {
      await api.login(formData.email, formData.password);
      
      // Just redirect to dashboard - affiliate status will be checked there
      navigate('/dashboard');
    } catch (err) {
      const errorMessage = err.message || 'Login failed. Please check your credentials.';

      if (errorMessage.toLowerCase().includes('verify your email')) {
        setError('');
        setVerificationMessage('Your email is not verified yet. Please check your inbox for the verification link.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingEmail(true);
    setError('');

    try {
      await api.sendVerificationEmail(formData.email, 'Influencer');
      setVerificationMessage('Verification email sent successfully. Please check your inbox.');
    } catch (err) {
      setError(err.message || 'Failed to resend verification email.');
    } finally {
      setResendingEmail(false);
    }
  };

  return (
    <SiteChrome>

      {/* Main Content */}
      <div className="max-w-md mx-auto px-5 sm:px-6 py-6 sm:py-8">
        <div className="rounded-[3px] border border-gray-100 bg-white shadow-xl shadow-slate-200/60 overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-primary-500 via-primary-600 to-slate-900" />
          <div className="p-4 sm:p-6">
          {/* Header */}
          <div className="text-center mb-5">
            <p className="text-xs font-semibold text-primary-700 mb-2">
              Affiliate Login
            </p>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              Sign in to your dashboard
            </h1>
            <p className="text-sm text-gray-600">
              Track referrals, commissions, and payout requests.
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {verificationMessage && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-blue-900 text-sm mb-3">{verificationMessage}</p>
              <p className="text-xs text-blue-700 mb-3">
                If you still do not see it, check your spam folder and confirm you are using the same email address you registered with.
              </p>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendingEmail}
                className="text-sm text-primary-600 hover:underline font-medium disabled:opacity-50"
              >
                {resendingEmail ? 'Sending...' : 'Resend Verification Email'}
              </button>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className={labelClass}>Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={inputClass}
                placeholder="john@example.com"
              />
            </div>

            <div>
              <label className={labelClass}>Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                className={inputClass}
                placeholder="Password"
              />
            </div>

            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="remember"
                  checked={formData.remember}
                  onChange={handleChange}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label className="ml-2 text-sm text-gray-600">Remember me</label>
              </div>
              <Link to="/forgot-password" className="text-sm text-primary-600 hover:underline">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-600 text-white text-sm py-3 rounded-[3px] font-semibold hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed shadow-lg shadow-primary-100"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          {/* Don't have account */}
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/signup" className="text-primary-600 hover:underline font-medium">
                Sign up here
              </Link>
            </p>
          </div>

          </div>
        </div>
      </div>
    </SiteChrome>
  );
};

export default Login;
